import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import type { AdminPermission } from '@shared/roles';
import { useAuth } from './auth';
import { AppShell } from './AppShell';
import { LoginPage } from '../pages/LoginPage';
import { DashboardPage } from '../pages/DashboardPage';
import { ProductsPage } from '../pages/ProductsPage';
import { OrdersPage } from '../pages/OrdersPage';
import { InvoicesPage } from '../pages/InvoicesPage';
import { CustomersPage } from '../pages/CustomersPage';
import { TenantsPage } from '../pages/TenantsPage';
import { TicketsPage } from '../pages/TicketsPage';
import { AuditPage } from '../pages/AuditPage';
import { StaffPage } from '../pages/StaffPage';
import { AccountPage } from '../pages/AccountPage';
import { SettingsPage } from '../pages/SettingsPage';
import { AssistantPage } from '../pages/AssistantPage';
import { ContentPage } from '../pages/ContentPage';

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'loading') {
    return <div className="flex min-h-full items-center justify-center text-sm text-fg-muted">正在加载…</div>;
  }
  if (status === 'anonymous') return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequirePermission({
  permission,
  children,
}: {
  permission: AdminPermission;
  children: ReactNode;
}) {
  const { can } = useAuth();
  if (!can(permission)) {
    return (
      <div className="rounded-xl bg-panel p-6 text-[13px] text-fg-muted">
        当前角色没有访问该页面的权限。需要的话请联系超级管理员调整角色。
      </div>
    );
  }
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="*"
        element={
          <RequireAuth>
            <AppShell>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route
                  path="/products"
                  element={
                    <RequirePermission permission="product.view">
                      <ProductsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <RequirePermission permission="order.view">
                      <OrdersPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/invoices"
                  element={
                    <RequirePermission permission="order.view">
                      <InvoicesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/customers"
                  element={
                    <RequirePermission permission="customer.view">
                      <CustomersPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/tenants"
                  element={
                    <RequirePermission permission="customer.view">
                      <TenantsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/tickets"
                  element={
                    <RequirePermission permission="ticket.view">
                      <TicketsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/audit"
                  element={
                    <RequirePermission permission="audit.view">
                      <AuditPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/staff"
                  element={
                    <RequirePermission permission="staff.manage">
                      <StaffPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <RequirePermission permission="settings.view">
                      <SettingsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/assistant"
                  element={
                    <RequirePermission permission="settings.view">
                      <AssistantPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/content"
                  element={
                    <RequirePermission permission="content.view">
                      <ContentPage />
                    </RequirePermission>
                  }
                />
                <Route path="/account" element={<AccountPage />} />
                <Route path="*" element={<div className="text-sm text-fg-muted">页面不存在</div>} />
              </Routes>
            </AppShell>
          </RequireAuth>
        }
      />
    </Routes>
  );
}
